import { useUser } from "@auth0/nextjs-auth0/client";
import { Badge, Button, Group, SimpleGrid, Tooltip } from "@mantine/core";
import Link from "next/link";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SectionTitle } from "@/components/general/section-title/section-title";
import { EscapeRoomCardProps } from "@/components/types";
import { useUserContext } from "@/context/user-context-provider";

import { Card } from "../../../general/card/card";
import { Section } from "../../../general/section/section";
import styles from "./escape-room-overview.module.css";

export const EscapeRoomOverview = ({ escapeRooms }: { escapeRooms: EscapeRoomCardProps[] }) => {
    const sortedRooms = useMemo(() => {
        return escapeRooms.filter((room) => room.url !== "kitchen").sort((a, b) => a.difficulty - b.difficulty);
    }, [escapeRooms]); //TODO: include Kitchen

    const bundleRooms = escapeRooms.filter((room) => ["witchhood", "buro", "island"].includes(room.url));
    const { t } = useTranslation();
    return (
        <Section variant="purple-pink" className={styles.section} id="escape-rooms">
            <SectionTitle title={t("home.escaperooms.title")} subTitle={t("home.escaperooms.subtitle")} />
            <SimpleGrid mt="xl" spacing="xl" cols={{ lg: 3, md: 2, sm: 1 }} className={styles.list}>
                <Bundle escapeRooms={bundleRooms} />
                {sortedRooms.map((escapeRoom, index) => (
                    <EscapeRoom key={index as any} {...escapeRoom} />
                ))}
            </SimpleGrid>
        </Section>
    );
};

export const EscapeRoom = ({ id, url, title, descriptionShort, image, time, price, difficulty }: EscapeRoomCardProps) => {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const bought = user?.escaperooms.includes(id);
    return (
        <Card
            title={title}
            description={descriptionShort}
            difficulty={difficulty}
            image={image}
            imageBadge={
                <>
                    <Badge className={styles.timeBadge} color="var(--color-primary-8)" size="xl">
                        ~{time} min.
                    </Badge>
                    {price > 0 && !bought && (
                        <Badge className={styles.priceBadge} color="var(--color-primary-8)" size="xl">
                            {t("home.buy.price", { price })}
                        </Badge>
                    )}
                    {price === 0 && (
                        <Badge className={styles.priceBadge} color="green" size="xl">
                            {t("home.buy.free")}
                        </Badge>
                    )}
                    {bought && (
                        <Badge className={styles.priceBadge} color="orange" size="xl">
                            {t("home.buy.bought")}
                        </Badge>
                    )}
                </>
            }>
            <Group justify="space-between" mt="auto" />
            <Button
                lts=".06rem"
                radius="md"
                mt="auto"
                size="lg"
                className={styles.playButton}
                fullWidth
                component={Link}
                href={`/details/${url}`}
                area-label={`${title} – Online Escape Room, ${t("endscreen.difficulty")} ${difficulty},  ca. ${time} min`}
                title={`${title} – Online Escape Room, ${t("endscreen.difficulty")} ${difficulty},  ca. ${time} min`}>
                {t("home.escaperooms.button")}
            </Button>
        </Card>
    );
};

export const Bundle = ({ escapeRooms }: { escapeRooms: EscapeRoomCardProps[] }) => {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const { user: session } = useUser();

    const escapeRoomIds = new Set(escapeRooms.map((room) => room.id));

    const hasBoughtSomething = user?.escaperooms?.some((id) => escapeRoomIds.has(id)) ?? false;

    const showBundle = !user || !hasBoughtSomething;
    if (!showBundle) {
        return null;
    }

    return (
        <Card
            title={t("home.buy.3for2Title")}
            description={t("home.buy.3for2Description")}
            highlights={escapeRooms.map((room) => room.title)}
            image={escapeRooms.map((room) => room.image)}
            imageBadge={
                <>
                    <Badge className={styles.timeBadge} color="var(--color-primary-8)" size="xl">
                        ~90 min.
                    </Badge>
                    <Badge className={styles.priceBadge} color="var(--color-primary-8)" size="xl">
                        <span className={styles.price} data-old-price="17.97">
                            {t("home.buy.price", { price: "11.98" })}
                        </span>
                    </Badge>
                </>
            }>
            <Group justify="space-between" mt="auto" />
            <Tooltip disabled={!!session} label={session ? null : t("details.room.tooltipNotLoggedIn")} withArrow>
                <Button
                    lts=".06rem"
                    radius="md"
                    mt="auto"
                    size="lg"
                    className={styles.playButton}
                    fullWidth
                    component={session ? Link : "button"}
                    disabled={!session}
                    title={t("home.buy.buyButton")}
                    href={
                        session
                            ? `https://buy.stripe.com/eVqfZhcpSchN9sV1xP9Ve03?prefilled_email=${session.email}&client_reference_id=${session.email}`
                            : "#"
                    }>
                    {t("home.buy.buyButton")}
                </Button>
            </Tooltip>
        </Card>
    );
};
